import React, { useState } from 'react';
import { ChevronDown, SlidersHorizontal, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
interface LedgerEntry {
  ledger_id: string;
  timestamp: string;
  category: string;
  asset: string;
  amount: number;
  fee: number;
  balance_after: number;
}
const mockLedgerData: LedgerEntry[] = [{
  ledger_id: 'LIKYYV',
  timestamp: '1/18/26 4:43 PM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: -0.589,
  fee: 0.3144,
  balance_after: 6.3091
}, {
  ledger_id: 'L3YZ2V',
  timestamp: '1/18/26 3:35 PM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.2125
}, {
  ledger_id: 'LL3CRC',
  timestamp: '1/18/26 11:35 AM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.2521
}, {
  ledger_id: 'LWDGLB',
  timestamp: '1/18/26 7:35 AM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.2917
}, {
  ledger_id: 'LAEQIV',
  timestamp: '1/18/26 3:35 AM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.3313
}, {
  ledger_id: 'DEP001',
  timestamp: '1/17/26 10:00 AM',
  category: 'Deposit',
  asset: 'Bitcoin',
  amount: 0.5,
  fee: 0,
  balance_after: 0.5
}, {
  ledger_id: 'WDR001',
  timestamp: '1/16/26 2:15 PM',
  category: 'Withdrawal',
  asset: 'Ethereum',
  amount: -2.5,
  fee: 0.005,
  balance_after: 10.2
}, {
  ledger_id: 'REW001',
  timestamp: '1/15/26 9:00 AM',
  category: 'Rewards',
  asset: 'Polkadot',
  amount: 1.2,
  fee: 0,
  balance_after: 150.5
}, {
  ledger_id: 'TRD001',
  timestamp: '1/14/26 11:30 AM',
  category: 'Spot trade',
  asset: 'US Dollar',
  amount: -5000,
  fee: 10,
  balance_after: 2331.3
}, {
  ledger_id: 'TRD002',
  timestamp: '1/14/26 11:30 AM',
  category: 'Spot trade',
  asset: 'Bitcoin',
  amount: 0.12,
  fee: 0,
  balance_after: 0.62
}];
const categories = ['Deposit', 'Withdrawal', 'Transfer', 'Spot trade', 'Margin trade', 'Margin rollover', 'Margin settle', 'Earn', 'Rewards', 'Reward bonus', 'Instant', 'NFT', 'Credit', 'Other'];
const assets = ['USD', 'USDT', 'BTC', 'ETH', 'SOL', 'BNB', 'DOT'];
export function LedgerTab() {
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showAssetFilter, setShowAssetFilter] = useState(false);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const clearFilters = () => {
    setSelectedAssets([]);
    setSelectedCategories([]);
    setDateFrom('');
    setDateTo('');
  };
  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };
  const toggleAsset = (asset: string) => {
    if (selectedAssets.includes(asset)) {
      setSelectedAssets(selectedAssets.filter(a => a !== asset));
    } else {
      setSelectedAssets([...selectedAssets, asset]);
    }
  };
  const selectAllCategories = () => setSelectedCategories([...categories]);
  const clearAllCategories = () => setSelectedCategories([]);
  const hasActiveFilters = selectedAssets.length > 0 || selectedCategories.length > 0 || dateFrom || dateTo;
  // Filter data
  const filteredData = mockLedgerData.filter(entry => {
    if (selectedAssets.length > 0) {
      // Simple check for asset name/symbol match
      const assetMatch = selectedAssets.some(a => entry.asset.toLowerCase().includes(a.toLowerCase()) || a === 'USD' && entry.asset === 'US Dollar');
      if (!assetMatch) return false;
    }
    if (selectedCategories.length > 0 && !selectedCategories.includes(entry.category)) {
      return false;
    }
    // Date filtering would go here
    return true;
  });
  const totalItems = filteredData.length;
  const itemsPerPage = 25;
  const currentPage = 1;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  return <div className="space-y-4" data-id="element-12">
      {/* Filters */}
      <div className="bg-background-secondary border border-border-subtle rounded-xl p-4" data-id="element-13">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-14">
          {/* Assets Filter */}
          <div className="relative" data-id="element-15">
            <button onClick={() => setShowAssetFilter(!showAssetFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showAssetFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-16">
              Assets
              {selectedAssets.length > 0 && <span className="bg-brand-purple text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-17">
                  {selectedAssets.length}
                </span>}
              <ChevronDown size={16} data-id="element-18" />
            </button>
            {showAssetFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowAssetFilter(false)} data-id="element-19" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 p-3 max-h-80 overflow-y-auto" data-id="element-20">
                  <div className="space-y-1" data-id="element-21">
                    {assets.map(asset => <button key={asset} onClick={() => toggleAsset(asset)} className="w-full flex items-center gap-3 p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-22">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedAssets.includes(asset) ? 'bg-brand-purple border-brand-purple' : 'border-text-secondary group-hover:border-text-primary'}`} data-id="element-23">
                          {selectedAssets.includes(asset) && <Check size={10} className="text-white" data-id="element-24" />}
                        </div>
                        <span className="text-sm text-text-primary" data-id="element-25">
                          {asset}
                        </span>
                      </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Categories Filter */}
          <div className="relative" data-id="element-26">
            <button onClick={() => setShowCategoryFilter(!showCategoryFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showCategoryFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-27">
              Categories
              {selectedCategories.length > 0 && <span className="bg-brand-purple text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-28">
                  {selectedCategories.length}
                </span>}
              <ChevronDown size={16} data-id="element-29" />
            </button>
            {showCategoryFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowCategoryFilter(false)} data-id="element-30" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 flex flex-col max-h-96" data-id="element-31">
                  <div className="p-3 border-b border-border-subtle flex gap-2" data-id="element-32">
                    <button onClick={selectAllCategories} className="text-xs text-brand-purple hover:text-brand-purple-light font-medium" data-id="element-33">
                      Select All
                    </button>
                    <span className="text-border-subtle" data-id="element-34">|</span>
                    <button onClick={clearAllCategories} className="text-xs text-text-secondary hover:text-text-primary font-medium" data-id="element-35">
                      Clear All
                    </button>
                  </div>
                  <div className="p-3 overflow-y-auto space-y-1" data-id="element-36">
                    {categories.map(category => <button key={category} onClick={() => toggleCategory(category)} className="w-full flex items-center gap-3 p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-37">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedCategories.includes(category) ? 'bg-brand-purple border-brand-purple' : 'border-text-secondary group-hover:border-text-primary'}`} data-id="element-38">
                          {selectedCategories.includes(category) && <Check size={10} className="text-white" data-id="element-39" />}
                        </div>
                        <span className="text-sm text-text-primary" data-id="element-40">
                          {category}
                        </span>
                      </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2" data-id="element-41">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" className="px-4 py-2 bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-42" />
            <span className="text-text-secondary" data-id="element-43">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" className="px-4 py-2 bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-44" />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-text-secondary hover:text-text-primary transition-colors text-sm" data-id="element-45">
              Clear filters
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-background-secondary border border-border-subtle rounded-xl overflow-hidden" data-id="element-46">
        <div className="overflow-x-auto" data-id="element-47">
          <table className="w-full" data-id="element-48">
            <thead className="bg-background-elevated/50" data-id="element-49">
              <tr className="border-b border-border-subtle" data-id="element-50">
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-51">
                  Date
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-52">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-53">
                  Asset
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-54">
                  Amount
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-55">
                  Fee
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-56">
                  Balance
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-57">
                  ID
                </th>
              </tr>
            </thead>
            <tbody data-id="element-58">
              {filteredData.map(entry => <tr key={entry.ledger_id} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-59">
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-60">
                    {entry.timestamp}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-61">
                    {entry.category}
                  </td>
                  <td className="px-6 py-4" data-id="element-62">
                    <div className="text-sm font-medium text-text-primary" data-id="element-63">
                      {entry.asset}
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-64">USD</div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-65">
                    <div className={`font-numbers text-sm ${entry.amount < 0 ? 'text-danger' : entry.amount > 0 ? 'text-success' : 'text-text-primary'}`} data-id="element-66">
                      {entry.amount.toFixed(4)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-67">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-68">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-69">
                      {entry.fee.toFixed(4)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-70">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-71">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-72">
                      {entry.balance_after.toFixed(4)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-73">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-74">
                    <div className="font-numbers text-xs text-text-secondary" data-id="element-75">
                      {entry.ledger_id}
                    </div>
                  </td>
                </tr>)}
              {filteredData.length === 0 && <tr data-id="element-76">
                  <td colSpan={7} className="px-6 py-8 text-center text-text-secondary" data-id="element-77">
                    No ledger entries found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-between" data-id="element-78">
          <div className="text-sm text-text-secondary" data-id="element-79">
            {totalItems > 0 ? `1 - ${Math.min(itemsPerPage, totalItems)} of ${totalItems}` : '0 of 0'}
          </div>
          <div className="flex items-center gap-2" data-id="element-80">
            <button disabled={currentPage === 1} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-81">
              <ChevronLeft size={16} data-id="element-82" />
            </button>
            <div className="text-sm font-medium text-text-primary px-2" data-id="element-83">
              Page {currentPage} of {totalPages}
            </div>
            <button disabled={currentPage === totalPages} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-84">
              <ChevronRight size={16} data-id="element-85" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}